/*
 * Partner Portal (Phase P-F) — READ-ONLY UI.
 *
 * Consumes the four approved P-E endpoints:
 *   GET /api/partner/me
 *   GET /api/partner/dashboard
 *   GET /api/partner/customers
 *   GET /api/partner/commissions
 *
 * Access is gated SERVER-SIDE by `require_partner_user`:
 *   401 → not signed in           → prompt to log in
 *   403 → not an active partner   → denied state
 *   200 → render portal
 *
 * This page NEVER accepts a partner_id from the client, never calls admin
 * endpoints, never calculates commission values locally, and never triggers
 * payouts / finalisation / Stripe. The 9-key /customers whitelist is
 * displayed verbatim — no customer-identifying field is added.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, ShieldAlert, LogIn, RefreshCw, Users as UsersIcon,
  PoundSterling, Activity, Inbox, AlertTriangle, ChevronLeft, ChevronRight,
  Link2, Copy, Check, Clock, CheckCircle2, XCircle, Pencil,
} from "lucide-react";
import { getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${getToken()}` });

// ---- Formatting helpers (presentation only — all math is server-side) ----
const fmtGbp = (v) => {
  if (v == null || v === "") return "—";
  const s = String(v);
  return s.startsWith("-") ? `-£${s.slice(1)}` : `£${s}`;
};
const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return "—"; }
};
const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};
const defaultPeriod = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};

// Mirrors the P-C ledger states exactly. Rendering only — the client cannot
// transition anything.
const STATUS_META = {
  pending:    { chip: "bg-amber-100 text-amber-900 border-amber-300", label: "Pending", help: "Being calculated this period — figures may change until finalised by MyAE." },
  approved:   { chip: "bg-indigo-100 text-indigo-900 border-indigo-300", label: "Approved", help: "Finalised by MyAE. Immutable." },
  available:  { chip: "bg-sky-100 text-sky-900 border-sky-300", label: "Available", help: "Approved and ready for payout processing by MyAE." },
  processing: { chip: "bg-violet-100 text-violet-900 border-violet-300", label: "Processing", help: "Payment being processed by MyAE." },
  paid:       { chip: "bg-emerald-100 text-emerald-900 border-emerald-300", label: "Paid", help: "Payment settled." },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { chip: "bg-muted text-foreground border-border", label: status || "—", help: "" };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border ${meta.chip}`}
      title={meta.help}
      data-testid={`commission-status-${status}`}
    >
      {meta.label}
    </span>
  );
};

const PartnerStatusPill = ({ status }) => {
  const map = {
    active:    "bg-emerald-100 text-emerald-900 border-emerald-300",
    suspended: "bg-amber-100 text-amber-900 border-amber-300",
    archived:  "bg-neutral-200 text-neutral-800 border-neutral-300",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border ${map[status] || "bg-muted text-foreground border-border"}`}
      data-testid="partner-status-pill"
    >
      {status || "—"}
    </span>
  );
};

const Stat = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="bg-card border border-border p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-muted-foreground mb-2">
      <Icon size={15} /> <span className="overline">{label}</span>
    </div>
    <p className="font-mono text-3xl font-semibold text-oxford tabular-nums break-all">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const InlineError = ({ message, onRetry }) => (
  <div
    className="border border-rose-300 bg-rose-50 text-rose-900 p-5 flex items-start gap-3"
    data-testid="portal-error-banner"
  >
    <AlertTriangle size={18} className="shrink-0 mt-0.5" />
    <div className="flex-1 text-sm">
      <p className="font-medium mb-1">Could not load this view.</p>
      <p className="text-rose-800/90">{message || "Please try again, or contact MyAE support if the problem continues."}</p>
    </div>
    {onRetry && (
      <button
        onClick={onRetry}
        className="text-sm underline decoration-rose-500 hover:text-rose-700"
        data-testid="portal-error-retry"
      >
        Retry
      </button>
    )}
  </div>
);

const Empty = ({ message, testId }) => (
  <div
    className="border border-dashed border-border bg-muted/30 p-10 text-center text-muted-foreground flex flex-col items-center gap-3"
    data-testid={testId}
  >
    <Inbox size={28} />
    <p className="text-sm">{message}</p>
  </div>
);

const BusyRow = () => (
  <div className="flex items-center justify-center gap-3 text-muted-foreground py-16">
    <Loader2 className="animate-spin" size={18} /> <span className="text-sm">Loading…</span>
  </div>
);

// ---------------------------------------------------------------------------
// Dashboard tab
// ---------------------------------------------------------------------------
const DashboardTab = ({ me }) => {
  const [period, setPeriod] = useState(defaultPeriod());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/partner/dashboard`, {
        headers: authHdr(),
        params: period ? { period } : {},
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Dashboard unavailable.");
      setData(null);
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const stats = data?.stats || {};
  const current = data?.current_commission;
  const rate = me?.partner?.commission_rate_pct;

  return (
    <div data-testid="portal-dashboard">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <p className="overline text-oxford mb-1">Partner overview</p>
          <h2 className="font-serif text-3xl tracking-tight">Dashboard</h2>
          <p className="text-sm text-muted-foreground mt-1">
            All figures are calculated by MyAE and reflect only your referred customers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground" htmlFor="dash-period">Period</label>
          <input
            id="dash-period"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-1.5 text-sm border border-border bg-background"
            data-testid="dashboard-period-input"
          />
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:bg-muted"
            data-testid="dashboard-refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {loading && <BusyRow />}
      {!loading && err && <InlineError message={err} onRetry={load} />}

      {!loading && !err && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-8">
            <Stat
              icon={UsersIcon}
              label="Attributed customers"
              value={stats.attributed_users ?? 0}
              sub="distinct accounts referred by you"
              testId="dash-stat-attributed"
            />
            <Stat
              icon={Activity}
              label="Paid customers"
              value={stats.attributed_paid ?? 0}
              sub={`${stats.attributed_payments ?? 0} paid transactions`}
              testId="dash-stat-paid"
            />
            <Stat
              icon={PoundSterling}
              label="Commission rate"
              value={rate != null ? `${rate}%` : "—"}
              sub="of eligible contribution profit"
              testId="dash-stat-rate"
            />
            <Stat
              icon={PoundSterling}
              label={`Commission (${data?.period || period})`}
              value={current ? fmtGbp(current.commission_gbp) : "£0.00"}
              sub={current ? STATUS_META[current.status]?.label || current.status : "No ledger row yet for this period."}
              testId="dash-stat-commission"
            />
          </div>

          <div className="border border-border bg-card p-6" data-testid="dash-current-row">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-serif text-xl">Current period · {data?.period || period}</h3>
              {current ? <StatusPill status={current.status} /> : <span className="text-xs text-muted-foreground">No row</span>}
            </div>
            {current ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
                <div>
                  <p className="overline text-muted-foreground mb-1">Gross revenue</p>
                  <p className="font-mono text-lg">{fmtGbp(current.gross_revenue_gbp)}</p>
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-1">Paid transactions</p>
                  <p className="font-mono text-lg">{current.payment_count ?? 0}</p>
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-1">Contribution profit</p>
                  <p className="font-mono text-lg">{fmtGbp(current.contribution_profit_gbp)}</p>
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-1">Your commission</p>
                  <p className="font-mono text-lg text-oxford font-semibold">{fmtGbp(current.commission_gbp)}</p>
                </div>
              </div>
            ) : (
              <Empty
                message="No commission ledger row for this period yet. MyAE calculates rows as paid transactions arrive."
                testId="dash-current-empty"
              />
            )}
            <p className="text-xs text-muted-foreground mt-4">
              Commission values are frozen once MyAE finalises the row. Pending rows may change
              as new transactions settle during the month.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Customers tab
// ---------------------------------------------------------------------------
const CustomersTab = () => {
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/partner/customers`, {
        headers: authHdr(),
        params: { limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Customers unavailable.");
      setData(null);
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const total = data?.total ?? 0;
  const rows = data?.customers ?? [];
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE + rows.length);

  return (
    <div data-testid="portal-customers">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <p className="overline text-oxford mb-1">Referred customers</p>
          <h2 className="font-serif text-3xl tracking-tight">Customers</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Minimum referral metadata only. For privacy, MyAE never shares a customer's email,
            name, assignments, grades, uploaded documents, or academic feedback with partners.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:bg-muted"
          data-testid="customers-refresh-btn"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading && <BusyRow />}
      {!loading && err && <InlineError message={err} onRetry={load} />}

      {!loading && !err && (
        rows.length === 0 ? (
          <Empty
            message="No referred customers yet. Share your partner link to start attributing visitors."
            testId="customers-empty"
          />
        ) : (
          <>
            <div className="border border-border bg-card overflow-x-auto" data-testid="customers-table-wrap">
              <table className="w-full text-sm" data-testid="customers-table">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Reference</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Attributed</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Last touch</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Source</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Paid</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-right">Txns</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">First payment</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground">Last payment</th>
                    <th className="px-4 py-3 font-medium text-muted-foreground text-right">Total paid</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr
                      key={c.customer_ref}
                      className="border-t border-border"
                      data-testid={`customer-row-${c.customer_ref}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs" data-testid="cell-customer-ref">{c.customer_ref}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDate(c.attributed_at)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDate(c.last_touch_at)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{c.source || "—"}</td>
                      <td className="px-4 py-3">
                        {c.has_paid ? (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium border bg-emerald-100 text-emerald-900 border-emerald-300">Yes</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium border bg-neutral-100 text-neutral-700 border-neutral-300">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{c.payment_count ?? 0}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDate(c.first_payment_at)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDate(c.last_payment_at)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtGbp(c.total_paid_gbp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground flex-wrap gap-2">
              <span data-testid="customers-pagination-summary">
                Showing {from}–{to} of {total}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-border disabled:opacity-50 hover:bg-muted"
                  data-testid="customers-prev-btn"
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span className="text-xs">Page {page + 1} of {totalPages}</span>
                <button
                  onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                  disabled={page + 1 >= totalPages}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-border disabled:opacity-50 hover:bg-muted"
                  data-testid="customers-next-btn"
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Commissions tab
// ---------------------------------------------------------------------------
const CommissionsTab = () => {
  const [period, setPeriod] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = { limit: 100 };
      if (period) params.period = period;
      if (status) params.status = status;
      const r = await axios.get(`${API}/partner/commissions`, {
        headers: authHdr(),
        params,
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Commissions unavailable.");
      setData(null);
    } finally { setLoading(false); }
  }, [period, status]);

  useEffect(() => { load(); }, [load]);

  const rows = data?.commissions ?? [];

  return (
    <div data-testid="portal-commissions">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <p className="overline text-oxford mb-1">Earnings ledger</p>
          <h2 className="font-serif text-3xl tracking-tight">Commissions</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Server-calculated by MyAE. Once a row is finalised the figures are immutable.
            MyAE processes payouts outside this portal.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="flex flex-col">
            <label className="text-xs text-muted-foreground mb-1" htmlFor="comm-period">Period</label>
            <input
              id="comm-period"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="px-3 py-1.5 text-sm border border-border bg-background"
              data-testid="commissions-period-input"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs text-muted-foreground mb-1" htmlFor="comm-status">Status</label>
            <select
              id="comm-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-1.5 text-sm border border-border bg-background"
              data-testid="commissions-status-select"
            >
              <option value="">All</option>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:bg-muted"
            data-testid="commissions-refresh-btn"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {loading && <BusyRow />}
      {!loading && err && <InlineError message={err} onRetry={load} />}

      {!loading && !err && (
        rows.length === 0 ? (
          <Empty
            message={
              period || status
                ? "No commission rows match the current filters."
                : "No commission rows yet. A row is created once MyAE records your first paid referral in a period."
            }
            testId="commissions-empty"
          />
        ) : (
          <div className="border border-border bg-card overflow-x-auto" data-testid="commissions-table-wrap">
            <table className="w-full text-sm" data-testid="commissions-table">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium text-muted-foreground">Period</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground text-right">Txns</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground text-right">Users</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground text-right">Gross</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground text-right">Contribution profit</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground text-right">Rate</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground text-right">Commission</th>
                  <th className="px-4 py-3 font-medium text-muted-foreground">Finalised</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-border"
                    data-testid={`commission-row-${r.id}`}
                  >
                    <td className="px-4 py-3 font-mono tabular-nums">{r.period || "—"}</td>
                    <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{r.payment_count ?? 0}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{r.user_count ?? 0}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtGbp(r.gross_revenue_gbp)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{fmtGbp(r.contribution_profit_gbp)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{r.commission_rate_pct != null ? `${r.commission_rate_pct}%` : "—"}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold text-oxford">{fmtGbp(r.commission_gbp)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{fmtDateTime(r.finalised_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      <p className="text-xs text-muted-foreground mt-4" data-testid="commissions-footnote">
        Status key — <span className="font-medium">Pending</span>: being calculated this period;
        <span className="font-medium"> Approved</span>: finalised and locked;
        <span className="font-medium"> Available</span>: ready for payout;
        <span className="font-medium"> Processing</span>: payment in progress;
        <span className="font-medium"> Paid</span>: settled.
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Referral link tab (P-G) — current link + admin-approved slug-change flow
// ---------------------------------------------------------------------------
// Client-side format mirror of backend `_SLUG_RE` for immediate UX feedback.
// The server is still authoritative — this is an aid, not a gate.
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;

const RequestStatusCard = ({ req }) => {
  if (!req) return null;
  const cfg = {
    pending:  { icon: Clock,        chip: "bg-amber-50 border-amber-300 text-amber-900",
                title: "Change pending admin approval" },
    approved: { icon: CheckCircle2, chip: "bg-emerald-50 border-emerald-300 text-emerald-900",
                title: "Change approved" },
    rejected: { icon: XCircle,      chip: "bg-rose-50 border-rose-300 text-rose-900",
                title: "Change rejected" },
  }[req.status] || { icon: Clock, chip: "bg-muted border-border text-foreground", title: req.status };
  const Icon = cfg.icon;
  return (
    <div
      className={`border p-4 flex items-start gap-3 ${cfg.chip}`}
      data-testid={`slug-request-status-${req.status}`}
    >
      <Icon size={18} className="shrink-0 mt-0.5" />
      <div className="flex-1 text-sm space-y-1">
        <p className="font-medium">{cfg.title}</p>
        <p>
          <span className="opacity-75">Current:</span>{" "}
          <code className="font-mono">{req.current_slug || "—"}</code>
          {" → "}
          <span className="opacity-75">Proposed:</span>{" "}
          <code className="font-mono">{req.proposed_slug}</code>
        </p>
        <p className="text-xs opacity-80">
          Submitted {fmtDateTime(req.requested_at)}
          {req.decided_at && ` · decided ${fmtDateTime(req.decided_at)}`}
          {req.decided_by_email && ` by ${req.decided_by_email}`}
        </p>
        {req.status === "rejected" && req.rejection_reason && (
          <p className="text-xs italic" data-testid="slug-request-rejection-reason">
            Reason: {req.rejection_reason}
          </p>
        )}
        {req.status === "pending" && (
          <p className="text-xs opacity-80">
            Your current referral link is unchanged until an admin approves this request.
          </p>
        )}
      </div>
    </div>
  );
};

const ConfirmSlugModal = ({ current, proposed, onCancel, onConfirm, busy }) => (
  <div
    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
    onClick={onCancel}
    data-testid="slug-confirm-modal"
  >
    <div
      className="bg-background border border-border max-w-md w-full p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="font-serif text-xl mb-3">Confirm slug change request</h3>
      <p className="text-sm text-muted-foreground mb-4">
        An admin must approve this change before your referral link updates. Your
        current link will keep working until then. Previously issued links will
        continue to attribute correctly after approval.
      </p>
      <div className="border border-border bg-muted/40 p-3 text-sm space-y-1 mb-5">
        <p><span className="text-muted-foreground">Current slug:</span> <code className="font-mono">{current || "—"}</code></p>
        <p><span className="text-muted-foreground">Proposed slug:</span> <code className="font-mono">{proposed}</code></p>
      </div>
      <div className="flex items-center justify-end gap-3">
        <button
          onClick={onCancel}
          disabled={busy}
          className="px-4 py-2 text-sm border border-border hover:bg-muted disabled:opacity-50"
          data-testid="slug-confirm-cancel"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={busy}
          className="px-4 py-2 text-sm bg-oxford text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
          data-testid="slug-confirm-submit"
        >
          {busy && <Loader2 className="animate-spin" size={14} />}
          Submit request
        </button>
      </div>
    </div>
  </div>
);

const ReferralLinkTab = ({ me }) => {
  const siteOrigin = "https://myae.co.uk";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [proposed, setProposed] = useState("");
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/partner/slug-request`, { headers: authHdr() });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Referral link info unavailable.");
      setData(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const liveSlug = data?.partner?.slug || me?.partner?.slug;
  const liveUrl = liveSlug ? `${siteOrigin}/r/${liveSlug}` : "";
  const pending = data?.pending_request || null;
  const latest = data?.latest_request || null;
  const canSubmit = !!data?.can_submit;
  const role = data?.role;

  const normalised = (proposed || "").trim().toLowerCase();
  const formatOk = normalised.length === 0 || SLUG_PATTERN.test(normalised);
  const sameAsCurrent = normalised && liveSlug && normalised === liveSlug;
  const disableSubmit = !canSubmit || submitting || !normalised
    || !formatOk || sameAsCurrent;

  const copy = () => {
    if (!liveUrl) return;
    navigator.clipboard?.writeText(liveUrl).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1800); toast.success("Link copied."); },
      () => toast.error("Copy failed."),
    );
  };

  const openConfirm = () => {
    if (disableSubmit) return;
    setConfirming(true);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/partner/slug-request`,
        { proposed_slug: normalised },
        { headers: authHdr() },
      );
      toast.success("Slug change request submitted. An admin will review it.");
      setProposed("");
      setConfirming(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="portal-referral-link">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <p className="overline text-oxford mb-1">Referral link</p>
          <h2 className="font-serif text-3xl tracking-tight">Your referral URL</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Share this link to attribute new MyAE customers to your partner account.
            Admins must approve any change to the slug — your current link keeps working
            and historical links remain valid after approval.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:bg-muted"
          data-testid="referral-refresh-btn"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {loading && <BusyRow />}
      {!loading && err && <InlineError message={err} onRetry={load} />}

      {!loading && !err && (
        <div className="space-y-6">
          {/* Current link block */}
          <div className="border border-border bg-card p-6" data-testid="referral-current-block">
            <div className="flex items-center gap-2 text-muted-foreground mb-2">
              <Link2 size={14} />
              <span className="overline">Current canonical link</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <code
                className="font-mono text-lg text-oxford break-all"
                data-testid="referral-current-url"
              >
                {liveUrl || "—"}
              </code>
              <button
                onClick={copy}
                disabled={!liveUrl}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-border hover:bg-muted disabled:opacity-50"
                data-testid="referral-copy-btn"
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? "Copied" : "Copy link"}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Slug: <code className="font-mono">{liveSlug || "—"}</code>. Previously issued
              links keep attributing to your partner after an approved rename.
            </p>
          </div>

          {/* Latest request status */}
          {latest && <RequestStatusCard req={latest} />}

          {/* Request-a-change form */}
          <div className="border border-border bg-card p-6" data-testid="referral-change-form">
            <div className="flex items-center gap-2 text-muted-foreground mb-3">
              <Pencil size={14} />
              <span className="overline">Request a slug change</span>
            </div>

            {role !== "partner_admin" && (
              <p
                className="text-sm text-amber-900 bg-amber-50 border border-amber-300 p-3 mb-3"
                data-testid="referral-viewer-notice"
              >
                Only partner administrators can request a slug change. Please ask your
                partner admin to submit this change on your behalf.
              </p>
            )}

            {pending && (
              <p
                className="text-sm text-muted-foreground mb-3"
                data-testid="referral-pending-notice"
              >
                A change request is already in review. Please wait for the admin decision
                before submitting another.
              </p>
            )}

            <label className="block text-xs text-muted-foreground mb-1" htmlFor="proposed-slug">
              Proposed slug
            </label>
            <div className="flex items-stretch gap-2 flex-wrap">
              <div className="flex items-stretch flex-1 min-w-[260px] border border-border bg-background">
                <span className="px-3 py-2 text-sm text-muted-foreground border-r border-border bg-muted/40 font-mono">
                  {siteOrigin}/r/
                </span>
                <input
                  id="proposed-slug"
                  type="text"
                  value={proposed}
                  onChange={(e) => setProposed(e.target.value)}
                  placeholder="your-new-slug"
                  disabled={!canSubmit}
                  className="flex-1 px-3 py-2 text-sm font-mono bg-background disabled:opacity-60 outline-none"
                  data-testid="referral-proposed-input"
                  maxLength={40}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />
              </div>
              <button
                onClick={openConfirm}
                disabled={disableSubmit}
                className="px-4 py-2 text-sm bg-oxford text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="referral-submit-btn"
              >
                Request change
              </button>
            </div>

            {normalised && !formatOk && (
              <p className="text-xs text-rose-700 mt-2" data-testid="referral-format-hint">
                Slug must be 3–40 characters, lowercase letters, digits or hyphens, and
                must not start or end with a hyphen.
              </p>
            )}
            {sameAsCurrent && (
              <p className="text-xs text-rose-700 mt-2" data-testid="referral-same-hint">
                Proposed slug matches your current slug.
              </p>
            )}
            <p className="text-xs text-muted-foreground mt-3">
              The admin team reviews each request to prevent impersonation and protect
              existing attribution. Reserved names (admin, myae, api, …) are blocked.
            </p>
          </div>
        </div>
      )}

      {confirming && (
        <ConfirmSlugModal
          current={liveSlug}
          proposed={normalised}
          onCancel={() => (submitting ? null : setConfirming(false))}
          onConfirm={submit}
          busy={submitting}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page shell + access gating
// ---------------------------------------------------------------------------
const TABS = ["Dashboard", "Referral", "Customers", "Commissions"];

export default function PartnerPortal() {
  const navigate = useNavigate();
  const { user, setAuthOpen } = useAuth();
  const [me, setMe] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | unauth | denied | error
  const [errMsg, setErrMsg] = useState("");
  const [tab, setTab] = useState("Dashboard");

  const loadMe = useCallback(async () => {
    setState("loading");
    if (!getToken()) { setState("unauth"); return; }
    try {
      const r = await axios.get(`${API}/partner/me`, { headers: authHdr() });
      setMe(r.data);
      setState("ok");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) setState("unauth");
      else if (code === 403) {
        setErrMsg(e?.response?.data?.detail || "You are not a partner member.");
        setState("denied");
      } else {
        setErrMsg(e?.response?.data?.detail || "The partner portal is temporarily unavailable.");
        setState("error");
      }
    }
  }, []);

  // Re-check membership whenever the signed-in identity changes (e.g. login
  // via the auth modal opened from the unauth state, or logout).
  const userKey = user ? (user.id || user.user_id || user.email || "u") : String(user);
  useEffect(() => { loadMe(); }, [loadMe, userKey]);

  // While AuthContext is still checking the session (user === null), hold.
  const authChecking = user === null;

  if (authChecking || state === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center gap-3 text-muted-foreground"
        data-testid="portal-loading"
      >
        <Loader2 className="animate-spin" /> Loading partner portal…
      </div>
    );
  }

  if (state === "unauth") {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-4 text-muted-foreground px-6 text-center"
        data-testid="portal-unauth"
      >
        <LogIn size={28} />
        <p className="text-sm max-w-md">
          Sign in to the MyAE account linked to your partner membership to open the portal.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAuthOpen({ open: true, mode: "login" })}
            className="px-4 py-2 bg-oxford text-white hover:opacity-90 text-sm"
            data-testid="portal-login-btn"
          >
            Sign in
          </button>
          <button
            onClick={() => navigate("/")}
            className="text-sm text-oxford hover:underline"
            data-testid="portal-home-btn"
          >
            Back to MyAE
          </button>
        </div>
      </div>
    );
  }

  if (state === "denied") {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-3 text-muted-foreground px-6 text-center"
        data-testid="portal-denied"
      >
        <ShieldAlert size={28} />
        <p className="text-sm max-w-md">{errMsg}</p>
        <p className="text-xs max-w-md">
          If you believe this is a mistake, please contact your MyAE account manager.
        </p>
        <button
          onClick={() => navigate("/")}
          className="text-sm text-oxford hover:underline"
          data-testid="portal-home-btn"
        >
          Back to MyAE
        </button>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center gap-3 text-muted-foreground px-6 text-center"
        data-testid="portal-fatal"
      >
        <AlertTriangle size={28} />
        <p className="text-sm max-w-md">{errMsg}</p>
        <button
          onClick={loadMe}
          className="px-4 py-2 bg-oxford text-white hover:opacity-90 text-sm"
          data-testid="portal-fatal-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  // state === "ok"
  const partnerLabel = me?.partner?.label || "Partner";
  const partnerStatus = me?.partner?.status;

  return (
    <div className="min-h-screen bg-background" data-testid="partner-portal">
      <div className="border-b border-border bg-card px-6 md:px-8 py-4 flex items-center justify-between flex-wrap gap-3">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-oxford hover:underline"
          data-testid="portal-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <span className="text-sm text-muted-foreground" data-testid="portal-signed-as">
          Signed in as {me?.user?.email || "—"} · <span className="font-medium">{me?.membership?.role || "member"}</span>
        </span>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <p className="overline text-oxford mb-2">Partner portal</p>
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight" data-testid="portal-partner-label">
            {partnerLabel}
          </h1>
          <PartnerStatusPill status={partnerStatus} />
        </div>
        <p className="text-sm text-muted-foreground mb-8 max-w-2xl">
          Welcome. This portal is read-only — figures below are calculated by MyAE and update
          automatically. No payouts are processed from this screen.
        </p>

        <div className="flex flex-wrap gap-1 border-b border-border mb-8" data-testid="portal-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`portal-tab-${t.toLowerCase()}`}
              className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
                tab === t
                  ? "border-oxford text-oxford"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Dashboard" && <DashboardTab me={me} />}
        {tab === "Referral" && <ReferralLinkTab me={me} />}
        {tab === "Customers" && <CustomersTab />}
        {tab === "Commissions" && <CommissionsTab />}

        <p className="text-xs text-muted-foreground mt-12 pt-6 border-t border-border">
          MyAE is a trading name of OpenDegree Ltd. Figures are calculated server-side from
          eligible payments; finalised commission rows are immutable. Partner members cannot see
          another partner's data.
        </p>
      </div>
    </div>
  );
}
